import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Toast, Button, PullToRefresh } from 'antd-mobile';
import request from '@/utils/request';
import { createNonceStr, md5, getLocationSearch } from '@/utils/utils';
import { useTranslation } from '@/i18n';
import './index.scss';

const Wallet = () => {
  const [userInfo, setUserInfo] = useState({});
  const [accountBalance, setAccountBalance] = useState({
    availableBalance: 0,
    balance: 0,
    frozenBalance: 0,
    fullTimeDriver: true,
    nextFreeBalance: 0
  });
  const [billList, setBillList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [billType, setBillType] = useState(0); // 0收入 1支出
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // 提现相关状态
  const [withdrawVisible, setWithdrawVisible] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [alipayAccount, setAlipayAccount] = useState('');
  const [driverName, setDriverName] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [amountError, setAmountError] = useState('');
  const [accountError, setAccountError] = useState('');
  const [nameError, setNameError] = useState('');

  const history = useHistory();
  const t = useTranslation();

  /**
   * 获取请求签名参数
   * @returns {Object} 包含签名的请求参数
   */
  const getSignParams = () => {
    let token = getLocationSearch().token || localStorage.getItem("userToken") || "";
    let appKey = "app_h5";
    let secret = "11dc72cf-88b0-46cd-91d7-b1a77fd69ee1";
    let timestamp = new Date().getTime();
    let random = createNonceStr();
    let tempStr = `appKey${appKey}secret${secret}timestamp${timestamp}random${random}token${token}`;
    let sign = md5(tempStr, 32).toString(16).toLocaleUpperCase();
    
    return {
      appKey: appKey,
      timestamp: timestamp,
      random: random,
      token: token,
      sign: sign
    };
  };

  useEffect(() => {
    // 初始化数据（只执行一次）
    initData();
  }, []);

  useEffect(() => {
    // 设置页面标题
    document.title = t('wallet.pageTitle');
    // 设置页面标题（如果在 WebView 中）
    if (typeof window?.savePicture?.setTitle === 'function') {
      window?.savePicture?.setTitle(t('wallet.pageTitle'));
    }
  }, [t]);

  useEffect(() => {
    // 监听 ESC 键关闭弹窗
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && withdrawVisible) {
        setWithdrawVisible(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [withdrawVisible]);

  // 初始化数据
  const initData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        getAccountBalance(),
        getBillList(0, 1, true)
      ]);
    } catch (error) {
      console.error('初始化数据失败', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取账户余额
  const getAccountBalance = async () => {
    try {
      const signParams = getSignParams();
      const token = signParams.token;
      const response = await request.get('/api/account/balance', { token }, signParams);
      if (response.data && response.data.errorCode === 0) {
        setAccountBalance(response.data.data);
      } else {
        console.error('获取账户余额失败', response);
        // 检查是否为认证失败，跳转登录页
        if (response.data?.errorCode === 110004) {
          Toast.offline(t('wallet.loginExpired'), 2);
          history.push('/login');
          return;
        }
        Toast.offline(response.data?.errorMsg || t('wallet.getBalanceFailed'), 2);
      }
    } catch (error) {
      console.error('获取账户余额失败', error);
      Toast.offline(t('wallet.getBalanceFailed'), 2);
    }
  };

  // 获取账单流水列表
  const getBillList = async (type = billType, page = pageNo, reset = false) => {
    try {
      const signParams = getSignParams();
      const token = signParams.token;
      const response = await request.post('/api/account/bills', {
        billType: type,
        pageNo: page,
        pageSize: 2000,
        countFlag:true,
        token
      }, signParams);
      
      if (response.data && response.data.errorCode === 0) {
        const newData = response.data.data || [];
        if (reset) {
          setBillList(newData);
        } else {
          setBillList(prev => [...prev, ...newData]);
        }
        
        // 判断是否还有更多数据
        setHasMore(newData.length === 20);
        setPageNo(page + 1);
      } else {
        console.error('获取账单流水失败', response);
        // 检查是否为认证失败，跳转登录页
        if (response.data?.errorCode === 110004) {
          Toast.offline(t('wallet.loginExpired'), 2);
          history.push('/login');
          return;
        }
        Toast.offline(response.data?.errorMsg || t('wallet.getBillsFailed'), 2);
      }
    } catch (error) {
      console.error('获取账单流水失败', error);
      Toast.offline(t('wallet.getBillsFailed'), 2);
    }
  };

  // 切换账单类型
  const handleBillTypeChange = (type) => {
    if (type === billType) return;
    
    setBillType(type);
    setPageNo(1);
    setBillList([]);
    setHasMore(true);
    getBillList(type, 1, true);
  };

  // 下拉刷新
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        getAccountBalance(),
        getBillList(billType, 1, true)
      ]);
      setPageNo(2);
    } catch (error) {
      console.error('刷新失败', error);
    } finally {
      setRefreshing(false);
    }
  };

  // 加载更多
  const loadMore = async () => {
    if (!hasMore || loading) return;
    setLoading(true);
    await getBillList(billType, pageNo);
    setLoading(false);
  };

  // 格式化金额（分转元）
  const formatAmount = (amount) => {
    return (amount / 100).toFixed(2);
  };

  // 格式化日期，只显示年月日
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    // 如果日期字符串包含时间部分，则只取日期部分
    if (dateStr.includes(' ')) {
      return dateStr.split(' ')[0];
    }
    
    // 如果是完整的日期时间格式，使用Date对象处理
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    
    return dateStr;
  };

  // 打开提现弹窗
  const openWithdrawModal = () => {
    if (accountBalance.availableBalance <= 0) {
      Toast.offline(t('wallet.noWithdrawableBalance'), 2);
      return;
    }
    setWithdrawVisible(true);
    // 清空之前的输入
    setWithdrawAmount('');
    setAlipayAccount('');
    setDriverName('');
  };

  // 关闭提现弹窗
  const closeWithdrawModal = () => {
    if (withdrawLoading) {
      return; // 提现过程中不允许关闭
    }
    setWithdrawVisible(false);
    setWithdrawAmount('');
    setAlipayAccount('');
    setDriverName('');
    setAmountError('');
    setAccountError('');
    setNameError('');
  };

  // 验证提现金额
  const validateWithdrawAmount = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return t('wallet.amountRequired');
    }
    if (numAmount < 1) {
      return t('wallet.amountMin');
    }
    const amountInCents = Math.round(numAmount * 100);
    if (amountInCents > accountBalance.availableBalance) {
      return t('wallet.amountExceed');
    }
    return null;
  };

  // 验证支付宝账号
  const validateAlipayAccount = (account) => {
    if (!account || account.trim().length === 0) {
      return t('wallet.accountRequired');
    }
    // 简单的邮箱或手机号验证
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^1[3-9]\d{9}$/;
    if (!emailRegex.test(account) && !phoneRegex.test(account)) {
      return t('wallet.accountInvalid');
    }
    return null;
  };

  // 验证真实姓名
  const validateDriverName = (name) => {
    if (!name || name.trim().length === 0) {
      return t('wallet.nameRequired');
    }
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      return t('wallet.nameMin');
    }
    if (trimmedName.length > 20) {
      return t('wallet.nameMax');
    }
    // 简单的中文姓名验证（允许中文、英文字母）
    const nameRegex = /^[\u4e00-\u9fa5a-zA-Z\s]+$/;
    if (!nameRegex.test(trimmedName)) {
      return t('wallet.nameInvalid');
    }
    return null;
  };

  // 处理提现金额输入
  const handleAmountChange = (e) => {
    const value = e.target.value;
    setWithdrawAmount(value);
    
    // 实时验证
    if (value) {
      const error = validateWithdrawAmount(value);
      setAmountError(error || '');
    } else {
      setAmountError('');
    }
  };

  // 处理支付宝账号输入
  const handleAccountChange = (e) => {
    const value = e.target.value;
    setAlipayAccount(value);
    
    // 实时验证
    if (value) {
      const error = validateAlipayAccount(value);
      setAccountError(error || '');
    } else {
      setAccountError('');
    }
  };

  // 处理姓名输入
  const handleNameChange = (e) => {
    const value = e.target.value;
    setDriverName(value);
    
    // 实时验证
    if (value) {
      const error = validateDriverName(value);
      setNameError(error || '');
    } else {
      setNameError('');
    }
  };

  // 提现操作
  const handleWithdraw = async () => {
    // 验证提现金额
    const amountError = validateWithdrawAmount(withdrawAmount);
    if (amountError) {
      Toast.offline(amountError, 2);
      return;
    }

    // 验证支付宝账号
    const accountError = validateAlipayAccount(alipayAccount);
    if (accountError) {
      Toast.offline(accountError, 2);
      return;
    }

    // 验证真实姓名
    const nameError = validateDriverName(driverName);
    if (nameError) {
      Toast.offline(nameError, 2);
      return;
    }

    const amount = parseFloat(withdrawAmount);
    const amountInCents = Math.round(amount * 100);

    setWithdrawLoading(true);
    Toast.loading(t('wallet.withdrawing'), 0, () => {}, true);
    try {
      const signParams = getSignParams();
      const token = signParams.token;
      const response = await request.post('/api/account/withdraw', {
        aliPayAccount: alipayAccount,
        amount: amountInCents,
        driverName: driverName.trim(),
        token
      }, signParams);

      Toast.hide();
      if (response.data && response.data.errorCode === 0) {
        Toast.success(t('wallet.withdrawSuccess'));
        closeWithdrawModal();
        // 刷新余额和账单
        await getAccountBalance();
        await getBillList(billType, 1, true);
        setPageNo(2);
      } else {
        console.error('提现失败', response);
        // 检查是否为认证失败，跳转登录页
        if (response.data?.errorCode === 110004) {
          Toast.offline(t('wallet.loginExpired'), 2);
          history.push('/login');
          return;
        }
        Toast.fail(t('wallet.withdrawFailed') + ': ' + (response.data?.errorMsg || t('wallet.unknownError')), 2);
      }
    } catch (error) {
      console.error('提现失败', error);
      Toast.hide();
      Toast.fail(t('wallet.withdrawFailed'), 2);
    } finally {
      setWithdrawLoading(false);
    }
  };

  // 返回上一页
  const goBack = () => {
    history.goBack();
  };

  // 获取操作类型文本
  const getOptionTypeText = (optionType) => {
    const typeMap = {
      1: t('wallet.receivedFare'),
      2: t('wallet.refund'),
      3: t('wallet.withdrawToAlipay'),
      4: t('wallet.receivedReward')
    };
    return typeMap[optionType] || t('wallet.other');
  };

  // 渲染账单项
  const renderBillItem = (bill) => {
    const isIncome = bill.billType === 0;
    const amount = bill.amount;
    const formattedAmount = formatAmount(Math.abs(amount));
    
    return (
      <div key={bill.id} className="bill-item">
        <div className="bill-content">
          <div className="bill-info">
            <h4 className="bill-title">{bill.optionContent || getOptionTypeText(bill.optionType)}</h4>
            <p className="bill-time">{bill.optionTime}</p>
            {bill.remark && <p className="bill-remark">{bill.remark}</p>}
          </div>
          <div className={`bill-amount ${isIncome ? 'income' : 'expense'}`}>
            {isIncome ? '+' : '-'}¥{formattedAmount}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="wallet-container">
      {/* 头部 */}
      {/* <div className="wallet-header">
        <div className="header-bar">
          <span className="back-btn" onClick={goBack}>←</span>
          <h1>司机钱包</h1>
          <div></div>
        </div>
      </div> */}

      <PullToRefresh
        onRefresh={onRefresh}
        refreshing={refreshing}
        distanceToRefresh={25}
        indicator={{
          activate: t('wallet.refreshActivate'),
          deactivate: t('wallet.refreshDeactivate'),
          release: t('wallet.refreshRelease'),
          finish: t('wallet.refreshFinish'),
        }}
      >
        <div className="wallet-content">
          {/* 余额卡片 */}
          <div className="balance-card">
            <div className="balance-header">
              <h2>{t('wallet.accountBalance')}</h2>
              {accountBalance.fullTimeDriver && (
                <span className="driver-type">{t('wallet.fullTimeDriver')}</span>
              )}
            </div>
            
            <div className="balance-amount">
              <span className="currency">¥</span>
              <span className="amount">{formatAmount(accountBalance.balance)}</span>
            </div>
            
            <div className="balance-details">
              <div className="detail-item">
                <span className="label">{t('wallet.availableBalance')}</span>
                <span className="value">¥{formatAmount(accountBalance.availableBalance)}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t('wallet.frozenBalance')}</span>
                <span className="value">¥{formatAmount(accountBalance.frozenBalance)}</span>
              </div>
              {accountBalance.nextFreeBalance > 0 && (
                <div className="detail-item">
                  <span className="label">{t('wallet.nextFreeBalance')}</span>
                  <span className="value">¥{formatAmount(accountBalance.nextFreeBalance)}</span>
                </div>
              )}
            </div>
            
            <div className="balance-actions">
              <Button 
                type="primary" 
                className="withdraw-btn"
                onClick={openWithdrawModal}
                disabled={accountBalance.availableBalance <= 0}
              >
                {t('wallet.withdrawBtn')}
              </Button>
            </div>
          </div>

          {/* 账单列表 */}
          <div className="bill-section">
            <div className="bill-header">
              <h3>{t('wallet.billDetails')}</h3>
              <div className="bill-tabs">
                <span 
                  className={`tab ${billType === 0 ? 'active' : ''}`}
                  onClick={() => handleBillTypeChange(0)}
                >
                  {t('wallet.income')}
                </span>
                <span 
                  className={`tab ${billType === 1 ? 'active' : ''}`}
                  onClick={() => handleBillTypeChange(1)}
                >
                  {t('wallet.expenditure')}
                </span>
              </div>
            </div>
            
            <div className="bill-list">
              {billList.length > 0 ? (
                billList.map(dateGroup => (
                  <div key={dateGroup.date} className="date-group">
                    <div className="date-header">{formatDate(dateGroup.date)}</div>
                    {dateGroup.billList.map(bill => renderBillItem(bill))}
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-icon" aria-hidden="true"></div>
                  <p>{billType === 0 ? t('wallet.noIncomeRecords') : t('wallet.noExpenditureRecords')}</p>
                </div>
              )}
              
              {hasMore && (
                <div className="load-more" onClick={loadMore}>
                  {loading ? t('wallet.loading') : t('wallet.loadMore')}
                </div>
              )}
            </div>
          </div>
        </div>
      </PullToRefresh>

      {/* 提现弹窗 */}
      {withdrawVisible && (
        <div className="withdraw-modal-overlay" onClick={closeWithdrawModal}>
          <div className="withdraw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('wallet.withdrawModalTitle')}</h3>
              <span className="close-btn" onClick={closeWithdrawModal}>×</span>
            </div>
            
            <div className="modal-body">
              <div className="withdraw-form">
                <div className="form-item">
                  <label>{t('wallet.withdraw')}</label>
                  <input
                    type="number"
                    placeholder={t('wallet.withdrawAmountPlaceholder')}
                    value={withdrawAmount}
                    onChange={handleAmountChange}
                    className={`form-input ${amountError ? 'error' : ''}`}
                    disabled={withdrawLoading}
                  />
                  <div className="available-amount">
                    {t('wallet.availableAmountLabel')}¥{formatAmount(accountBalance.availableBalance)}
                  </div>
                  {amountError && <div className="error-message">{amountError}</div>}
                  {withdrawAmount && !amountError && (
                    <div className="success-message">{t('wallet.amountValid')}</div>
                  )}
                </div>
                
                <div className="form-item">
                  <label>支付宝账号</label>
                  <input
                    type="text"
                    placeholder={t('wallet.alipayAccountPlaceholder')}
                    value={alipayAccount}
                    onChange={handleAccountChange}
                    className={`form-input ${accountError ? 'error' : ''}`}
                    disabled={withdrawLoading}
                  />
                  {accountError && <div className="error-message">{accountError}</div>}
                  {alipayAccount && !accountError && (
                    <div className="success-message">{t('wallet.accountValid')}</div>
                  )}
                </div>
                
                <div className="form-item">
                  <label>真实姓名</label>
                  <input
                    type="text"
                    placeholder={t('wallet.realNamePlaceholder')}
                    value={driverName}
                    onChange={handleNameChange}
                    className={`form-input ${nameError ? 'error' : ''}`}
                    disabled={withdrawLoading}
                  />
                  {nameError && <div className="error-message">{nameError}</div>}
                  {driverName && !nameError && (
                    <div className="success-message">{t('wallet.nameValid')}</div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-btn" 
                onClick={closeWithdrawModal}
                disabled={withdrawLoading}
              >
                {t('wallet.cancel')}
              </button>
              <button 
                className="confirm-btn" 
                onClick={handleWithdraw}
                disabled={withdrawLoading}
              >
                {withdrawLoading ? t('wallet.withdrawing') : t('wallet.confirmWithdraw')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
